import { imageModelForTask } from '@shared/imageRatioSelection';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Brush, Eraser, Undo2, Redo2, Trash2, Loader2, X, Check } from 'lucide-react';
import { useViewport } from '@xyflow/react';
import { useCanvasStore } from './canvas-store';
import { useActiveProjectImages, useActiveProjectId, useActiveProjectConversationHistory } from './store-selectors';

interface MaskStroke {
  x: number;
  y: number;
  size: number;
  erase: boolean;
}

interface BrushTool {
  type: 'draw' | 'erase';
}

interface CanvasBrushModeProps {
  onClose: () => void;
}

export function CanvasBrushMode({ onClose }: CanvasBrushModeProps) {
  const { selectedImageIds, setActiveTool, addImageNextTo, setSelectedImageIds } = useCanvasStore();
  const images = useActiveProjectImages();
  const viewport = useViewport();

  const selectedImage = images.find((img) => img.id === selectedImageIds[0]);

  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);

  const [brushTool, setBrushTool] = useState<BrushTool>({ type: 'draw' });
  const [brushSize, setBrushSize] = useState(30);
  const [maskStrokes, setMaskStrokes] = useState<MaskStroke[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [prompt, setPrompt] = useState('');

  const [history, setHistory] = useState<MaskStroke[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(historyIndex);
  const maskStrokesRef = useRef(maskStrokes);
  historyRef.current = history;
  historyIndexRef.current = historyIndex;
  maskStrokesRef.current = maskStrokes;

  const [isApplying, setIsApplying] = useState(false);
  const [applyDone, setApplyDone] = useState(false);
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);

  const nodeX = selectedImage?.x ?? 0;
  const nodeY = selectedImage?.y ?? 0;
  const nodeW = selectedImage?.displayWidth ?? 256;
  const nodeH = selectedImage?.displayHeight ?? 256;

  const screenX = nodeX * viewport.zoom + viewport.x;
  const screenY = nodeY * viewport.zoom + viewport.y;
  const screenW = nodeW * viewport.zoom;
  const screenH = nodeH * viewport.zoom;

  const drawMaskOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = screenW * dpr;
    canvas.height = screenH * dpr;
    canvas.style.width = `${screenW}px`;
    canvas.style.height = `${screenH}px`;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, screenW, screenH);

    const strokes = maskStrokesRef.current;
    if (strokes.length === 0) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = screenW;
    tempCanvas.height = screenH;
    const tCtx = tempCanvas.getContext('2d')!;

    strokes.filter((s) => !s.erase).forEach((stroke) => {
      tCtx.fillStyle = 'white';
      tCtx.beginPath();
      tCtx.arc(stroke.x, stroke.y, stroke.size / 2, 0, 2 * Math.PI);
      tCtx.fill();
    });
    strokes.filter((s) => s.erase).forEach((stroke) => {
      tCtx.globalCompositeOperation = 'destination-out';
      tCtx.beginPath();
      tCtx.arc(stroke.x, stroke.y, stroke.size / 2, 0, 2 * Math.PI);
      tCtx.fill();
      tCtx.globalCompositeOperation = 'source-over';
    });

    const overlayTemp = document.createElement('canvas');
    overlayTemp.width = screenW;
    overlayTemp.height = screenH;
    const oCtx = overlayTemp.getContext('2d')!;
    oCtx.fillStyle = 'rgba(239,68,68,0.35)';
    oCtx.fillRect(0, 0, screenW, screenH);
    oCtx.globalCompositeOperation = 'destination-in';
    oCtx.drawImage(tempCanvas, 0, 0);

    ctx.drawImage(overlayTemp, 0, 0);
  }, [screenW, screenH]);

  useEffect(() => {
    drawMaskOverlay();
  }, [drawMaskOverlay, maskStrokes, screenW, screenH, viewport]);

  const addToHistory = useCallback((strokes: MaskStroke[]) => {
    const idx = historyIndexRef.current;
    const prev = historyRef.current;
    const newHistory = prev.slice(0, idx + 1);
    newHistory.push([...strokes]);
    if (newHistory.length > 50) newHistory.shift();
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, []);

  const undoStroke = useCallback(() => {
    const idx = historyIndexRef.current;
    if (idx > 0) {
      const newIndex = idx - 1;
      const state = historyRef.current[newIndex];
      setMaskStrokes(state);
      maskStrokesRef.current = state;
      setHistoryIndex(newIndex);
    }
  }, []);

  const redoStroke = useCallback(() => {
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    if (idx < hist.length - 1) {
      const newIndex = idx + 1;
      const state = hist[newIndex];
      setMaskStrokes(state);
      maskStrokesRef.current = state;
      setHistoryIndex(newIndex);
    }
  }, []);

  const clearMask = useCallback(() => {
    setMaskStrokes([]);
    maskStrokesRef.current = [];
    addToHistory([]);
  }, [addToHistory]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveTool('select');
        onClose();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undoStroke();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redoStroke();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, setActiveTool, undoStroke, redoStroke]);

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const coords = getCanvasCoords(e);
    if (!coords) return;
    const stroke: MaskStroke = { x: coords.x, y: coords.y, size: brushSize * viewport.zoom, erase: brushTool.type === 'erase' };
    const next = [...maskStrokesRef.current, stroke];
    setMaskStrokes(next);
    maskStrokesRef.current = next;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e);
    if (coords) setCursorPos(coords);
    if (!isDrawing) return;
    if (!coords) return;
    const stroke: MaskStroke = { x: coords.x, y: coords.y, size: brushSize * viewport.zoom, erase: brushTool.type === 'erase' };
    const next = [...maskStrokesRef.current, stroke];
    setMaskStrokes(next);
    maskStrokesRef.current = next;
  };

  const handleMouseLeave = () => {
    setCursorPos(null);
    if (isDrawing) {
      setIsDrawing(false);
      addToHistory(maskStrokesRef.current);
    }
  };

  const handleMouseUp = () => {
    if (isDrawing) {
      setIsDrawing(false);
      addToHistory(maskStrokesRef.current);
    }
  };

  const getMaskDataUrl = (): string => {
    const naturalW = selectedImage?.originalWidth || nodeW;
    const naturalH = selectedImage?.originalHeight || nodeH;
    const out = document.createElement('canvas');
    out.width = naturalW;
    out.height = naturalH;
    const outCtx = out.getContext('2d')!;
    outCtx.fillStyle = 'black';
    outCtx.fillRect(0, 0, naturalW, naturalH);

    const scaleX = naturalW / screenW;
    const scaleY = naturalH / screenH;

    const drawStrokes = maskStrokesRef.current.filter((s) => !s.erase);
    drawStrokes.forEach((stroke) => {
      outCtx.fillStyle = 'white';
      outCtx.beginPath();
      outCtx.arc(stroke.x * scaleX, stroke.y * scaleY, (stroke.size / 2) * scaleX, 0, 2 * Math.PI);
      outCtx.fill();
    });
    maskStrokesRef.current.filter((s) => s.erase).forEach((stroke) => {
      outCtx.globalCompositeOperation = 'destination-out';
      outCtx.beginPath();
      outCtx.arc(stroke.x * scaleX, stroke.y * scaleY, (stroke.size / 2) * scaleX, 0, 2 * Math.PI);
      outCtx.fill();
      outCtx.globalCompositeOperation = 'source-over';
    });
    return out.toDataURL('image/png');
  };

  const handleApply = async () => {
    if (!selectedImage || !prompt.trim() || maskStrokes.length === 0) return;
    setIsApplying(true);
    try {
      const maskDataUrl = getMaskDataUrl();
      if (!maskDataUrl) throw new Error('No mask drawn');

      setSelectedImageIds([selectedImage.id]);

      const state = (await import('./canvas-store')).useCanvasStore.getState();
      const imageSelection = imageModelForTask(state, state.activeProjectId);
      const projForBrush = state.activeProjectId != null ? state.projects[state.activeProjectId] : null;
      const selectedReferenceUrls = [selectedImage.src].filter((s) => s.startsWith('http'));

      const response = await fetch('/api/canvas/agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Inpaint this area: ${prompt.trim()}`,
          projectId: state.activeProjectId,
          brandId: state.activeBrandId,
          adAccountId: state.activeAdAccountId,
          conversationHistory: projForBrush?.conversationHistory ?? [],
          imageModel: imageSelection.model,
          imageTier: imageSelection.tier,
          imageResolution: imageSelection.resolution,
          selectedAspectRatio: state.selectedAspectRatio,
          selectedAspectRatios: state.selectedAspectRatios,
          reference_images: selectedReferenceUrls,
          mask_data: maskDataUrl,
        }),
      });

      if (!response.ok) throw new Error(`Inpaint agent request failed: ${response.status}`);

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });
        const blocks = sseBuffer.split('\n\n');
        sseBuffer = blocks.pop() ?? '';
        for (const block of blocks) {
          if (!block.trim()) continue;
          const eventLine = block.split('\n').find((l) => l.startsWith('event:'));
          const dataLine = block.split('\n').find((l) => l.startsWith('data:'));
          if (!eventLine || !dataLine) continue;
          const eventType = eventLine.replace('event:', '').trim();
          try {
            const data = JSON.parse(dataLine.replace('data:', '').trim());
            if (eventType === 'canvas_image_ready' && data.imageUrl) {
              addImageNextTo(
                data.imageUrl,
                selectedImage.originalWidth,
                selectedImage.originalHeight,
                `brush-edit-${Date.now()}.jpg`,
                selectedImage.id,
                selectedImage.originalWidth,
                selectedImage.originalHeight,
                prompt.trim(),
              );
            }
          } catch {}
        }
      }

      setApplyDone(true);
    } catch (err) {
      console.error('[BrushMode] Inpaint error:', err);
    } finally {
      setIsApplying(false);
    }
  };

  const handleDone = () => {
    setActiveTool('select');
    onClose();
  };

  const handleClose = () => {
    setActiveTool('select');
    onClose();
  };

  if (!selectedImage) {
    return (
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 200,
          background: 'rgba(17,24,39,0.92)',
          color: '#FFFFFF',
          borderRadius: 12,
          padding: '12px 20px',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span>Select an image on the canvas first</span>
        <button
          onClick={handleClose}
          style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: 2 }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="canvas-brush-overlay-backdrop"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 30,
          background: 'rgba(0,0,0,0.25)',
          pointerEvents: 'none',
        }}
      />

      <canvas
        ref={overlayCanvasRef}
        data-testid="canvas-brush-overlay"
        width={screenW}
        height={screenH}
        style={{
          position: 'absolute',
          left: screenX,
          top: screenY,
          width: screenW,
          height: screenH,
          zIndex: 50,
          cursor: 'none',
          borderRadius: 8,
          border: '2px solid rgba(239,68,68,0.6)',
          boxSizing: 'border-box',
          pointerEvents: 'all',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      />

      <canvas ref={maskCanvasRef} width={screenW} height={screenH} style={{ display: 'none' }} />

      {cursorPos && (
        <div
          style={{
            position: 'absolute',
            left: screenX + cursorPos.x - (brushSize * viewport.zoom) / 2,
            top: screenY + cursorPos.y - (brushSize * viewport.zoom) / 2,
            width: brushSize * viewport.zoom,
            height: brushSize * viewport.zoom,
            borderRadius: '50%',
            border: `2px solid ${brushTool.type === 'draw' ? 'rgba(239,68,68,0.8)' : 'rgba(59,130,246,0.8)'}`,
            background: brushTool.type === 'draw' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
            pointerEvents: 'none',
            zIndex: 60,
          }}
        />
      )}

      <div
        data-testid="canvas-brush-controls"
        style={{
          position: 'absolute',
          bottom: 80,
          left: '50%',
          transform: 'translateX(-50%)',
          // v3.87 — fixed-width children (~670px intrinsic) overflowed the
          // right edge / slid under the chat panel (zIndex 500) on narrow
          // widths. Clamp + wrap instead of bleeding offscreen.
          maxWidth: 'calc(100vw - 32px)',
          flexWrap: 'wrap',
          justifyContent: 'center',
          zIndex: 600,
          background: 'rgba(17,24,39,0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: 16,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <button
          data-testid="brush-tool-draw"
          onClick={() => setBrushTool({ type: 'draw' })}
          title="Paint mask"
          style={{
            width: 32, height: 32, borderRadius: 8, border: 'none',
            background: brushTool.type === 'draw' ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.06)',
            color: brushTool.type === 'draw' ? '#EF4444' : '#9CA3AF',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Brush style={{ width: 14, height: 14 }} />
        </button>
        <button
          data-testid="brush-tool-erase"
          onClick={() => setBrushTool({ type: 'erase' })}
          title="Erase mask"
          style={{
            width: 32, height: 32, borderRadius: 8, border: 'none',
            background: brushTool.type === 'erase' ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.06)',
            color: brushTool.type === 'erase' ? '#3B82F6' : '#9CA3AF',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Eraser style={{ width: 14, height: 14 }} />
        </button>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.12)' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{brushSize}px</span>
          <input
            data-testid="brush-size-slider"
            type="range"
            min={10}
            max={80}
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            style={{ width: 80, accentColor: '#EF4444', cursor: 'pointer' }}
          />
        </div>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.12)' }} />

        <button
          data-testid="brush-undo"
          onClick={undoStroke}
          disabled={historyIndex <= 0}
          title="Undo (Cmd+Z)"
          style={{
            width: 30, height: 30, borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)',
            color: historyIndex <= 0 ? '#4B5563' : '#9CA3AF',
            cursor: historyIndex <= 0 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Undo2 style={{ width: 13, height: 13 }} />
        </button>
        <button
          data-testid="brush-redo"
          onClick={redoStroke}
          disabled={historyIndex >= history.length - 1}
          title="Redo (Cmd+Shift+Z)"
          style={{
            width: 30, height: 30, borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)',
            color: historyIndex >= history.length - 1 ? '#4B5563' : '#9CA3AF',
            cursor: historyIndex >= history.length - 1 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Redo2 style={{ width: 13, height: 13 }} />
        </button>
        <button
          data-testid="brush-clear"
          onClick={clearMask}
          title="Clear mask"
          style={{
            width: 30, height: 30, borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)',
            color: '#9CA3AF',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Trash2 style={{ width: 13, height: 13 }} />
        </button>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.12)' }} />

        <input
          data-testid="brush-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="What to fill in…"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleApply(); } }}
          style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            padding: '6px 10px',
            color: '#FFFFFF',
            fontSize: 12,
            width: 160,
            outline: 'none',
          }}
        />

        <button
          data-testid="brush-apply"
          onClick={handleApply}
          disabled={isApplying || maskStrokes.length === 0 || !prompt.trim()}
          title="Apply inpaint"
          style={{
            height: 32, borderRadius: 8, border: 'none',
            background: applyDone ? '#059669' : 'rgba(99,102,241,0.9)',
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 600,
            padding: '0 12px',
            cursor: isApplying || maskStrokes.length === 0 || !prompt.trim() ? 'not-allowed' : 'pointer',
            opacity: isApplying || maskStrokes.length === 0 || !prompt.trim() ? 0.5 : 1,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {isApplying ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : applyDone ? <Check style={{ width: 13, height: 13 }} /> : null}
          {isApplying ? 'Applying…' : applyDone ? 'Done!' : 'Apply'}
        </button>

        <button
          data-testid="brush-done"
          onClick={applyDone ? handleDone : handleClose}
          title={applyDone ? 'Close brush mode' : 'Cancel'}
          style={{
            width: 30, height: 30, borderRadius: 8, border: 'none',
            background: 'rgba(255,255,255,0.06)',
            color: '#9CA3AF',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X style={{ width: 13, height: 13 }} />
        </button>
      </div>
    </>
  );
}
